import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDataSourceTemplateActivation, useDataSourceTemplateDetail } from "~/features/dataSourceTemplates/hooks";
import { toDataSourceTemplateDetailUI } from "~/features/dataSourceTemplates/utils/transformers";
import { DataSourceTemplateDetailPresenter } from "./DataSourceTemplateDetailPresenter";
import { DataSourceTemplateFormDialog } from "./DataSourceTemplateFormDialog";

interface DataSourceTemplateDetailProps {
  dataSourceTemplateId: string;
  /** Back action: close the sheet (peek) or navigate to the list (full page). */
  onBack: () => void;
}

/**
 * Reusable from both the full-page route and the Peek side panel. Owns data,
 * the edit dialog, and the activation confirm flow; renders the presenter.
 */
export function DataSourceTemplateDetail({ dataSourceTemplateId, onBack }: DataSourceTemplateDetailProps) {
  const { t } = useTranslation();
  const { data } = useDataSourceTemplateDetail(dataSourceTemplateId);
  const { onActivate, onDeactivate, processing } = useDataSourceTemplateActivation();
  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const raw = data.dataSourceTemplates[0];
  const detail = useMemo(() => (raw ? toDataSourceTemplateDetailUI(raw) : null), [raw]);

  if (!detail) {
    return (
      <div className="rounded-[var(--radius-md)] border border-border-default bg-white px-5 py-12 text-center text-14-regular text-fg-2">
        {t("ui.notFound")}
      </div>
    );
  }

  const handleConfirm = async () => {
    if (pendingAction === "activate") {
      await onActivate({ id: detail.id });
    } else if (pendingAction === "deactivate") {
      await onDeactivate({ id: detail.id });
    }
    setPendingAction(null);
  };

  return (
    <>
      <DataSourceTemplateDetailPresenter
        template={detail}
        onBack={onBack}
        onEdit={() => setEditOpen(true)}
        onActivate={() => setPendingAction("activate")}
        onDeactivate={() => setPendingAction("deactivate")}
        pendingAction={pendingAction}
        confirmLabel={
          pendingAction === "activate" ? t("ui.activateX", { x: detail.name }) : t("ui.deactivateX", { x: detail.name })
        }
        processing={processing}
        onConfirm={handleConfirm}
        onCancelConfirm={() => setPendingAction(null)}
      />
      <DataSourceTemplateFormDialog open={editOpen} onOpenChange={setEditOpen} dataSourceTemplateId={detail.id} />
    </>
  );
}
